import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import SuperAdminLayout from '../SuperAdminLayout';
import { saRequest } from '../../../utils/superAdminAxiosCaller';
import { clearSuperAdminToken } from '../../../utils/superAdminToken';
import { parseSuperAdminError } from '../../../utils/superAdminErrors';

interface CompanyStats {
    total: number;
    active: number;
    suspended: number;
    inactive: number;
}

interface AuditEntry {
    id: number;
    action: string;
    actor_email: string | null;
    target_type: string | null;
    target_id: number | null;
    target_company_id: number | null;
    target_company_name: string | null;
    metadata: Record<string, any> | null;
    occurred_at: string;
}

interface DashboardResponse {
    companies: CompanyStats;
    recent_audit: AuditEntry[];
}

interface Me {
    id: number;
    name: string;
    email: string;
    last_login_at: string | null;
    last_login_ip: string | null;
}

// ── Action → (icon glyph, sentence fragment) ───────────────────────────────
// Keep glyphs monospace-friendly. No emojis.
type Verb = { glyph: string; verb: string; targetStyle: 'company' | 'user' | 'self' | 'none' };

const VERBS: Record<string, Verb> = {
    'sa.login.success':              { glyph: '→', verb: 'signed in',                     targetStyle: 'self' },
    'sa.login.failed':               { glyph: '✕', verb: 'failed to sign in',             targetStyle: 'self' },
    'sa.login.new_ip_alert_sent':    { glyph: '!', verb: 'triggered a new-IP alert',      targetStyle: 'self' },
    'sa.login.new_ip_alert_skipped': { glyph: '·', verb: 'signed in from a new IP',       targetStyle: 'self' },
    'sa.logout':                     { glyph: '↗', verb: 'signed out',                    targetStyle: 'self' },
    'sa.totp.enrolled':              { glyph: '◈', verb: 'enrolled two-factor',           targetStyle: 'self' },
    'sa.totp.recovery_code_used':    { glyph: '◆', verb: 'used a recovery code',          targetStyle: 'self' },
    'sa.totp.reset_cli':             { glyph: '↻', verb: 'had TOTP reset via CLI',        targetStyle: 'self' },
    'sa.known_ip.revoked':           { glyph: '✕', verb: 'revoked a trusted IP',          targetStyle: 'self' },
    'sa.bootstrap.cli_create':       { glyph: '+', verb: 'was bootstrapped via CLI',      targetStyle: 'self' },

    'sa.company.created':          { glyph: '+', verb: 'created',           targetStyle: 'company' },
    'sa.company.updated':          { glyph: '~', verb: 'edited',            targetStyle: 'company' },
    'sa.company.suspended':        { glyph: '‖', verb: 'suspended',         targetStyle: 'company' },
    'sa.company.marked_inactive':  { glyph: '○', verb: 'marked inactive',   targetStyle: 'company' },
    'sa.company.reactivated':      { glyph: '▸', verb: 'reactivated',       targetStyle: 'company' },

    'sa.user.created':             { glyph: '+', verb: 'added an admin to', targetStyle: 'company' },
    'sa.user.password_reset':      { glyph: '~', verb: 'reset a password on', targetStyle: 'company' },
};

function relativeTime(iso: string): string {
    const then = new Date(iso);
    if (isNaN(then.getTime())) return iso;
    const secs = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    return then.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function absoluteTime(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
}

// ── Stat tile ──────────────────────────────────────────────────────────────
const StatTile: React.FC<{
    label: string;
    value: number;
    primary?: boolean;
    hint?: string;
}> = ({ label, value, primary, hint }) => (
    <div className={`sa-stat${primary ? ' sa-stat-primary' : ''}`}>
        <div className="sa-stat-label">{label}</div>
        <div className="sa-stat-value">{value}</div>
        {hint && <div className="sa-stat-delta">{hint}</div>}
    </div>
);

// ── Feed item ──────────────────────────────────────────────────────────────
const FeedItem: React.FC<{ entry: AuditEntry }> = ({ entry }) => {
    const meta = VERBS[entry.action];
    const glyph = meta?.glyph ?? '·';
    const actor = entry.actor_email ?? 'system';

    let body: React.ReactNode;
    if (meta?.targetStyle === 'company' && (entry.target_company_id || entry.target_company_name)) {
        const companyLabel = entry.target_company_name ?? `#${entry.target_company_id}`;
        body = (
            <>
                <span className="sa-mono" style={{ color: 'var(--sa-text-secondary)' }}>{actor}</span>
                {' '}
                <span style={{ color: 'var(--sa-text-secondary)' }}>{meta.verb}</span>
                {' '}
                {entry.target_company_id ? (
                    <Link
                        to={`/mika/ops/companies/${entry.target_company_id}`}
                        style={{ color: 'var(--sa-text)', textDecoration: 'none', fontWeight: 500 }}
                    >
                        {companyLabel}
                    </Link>
                ) : (
                    <span style={{ color: 'var(--sa-text)', fontWeight: 500 }}>{companyLabel}</span>
                )}
            </>
        );
    } else {
        body = (
            <>
                <span className="sa-mono" style={{ color: 'var(--sa-text-secondary)' }}>{actor}</span>
                {' '}
                <span style={{ color: 'var(--sa-text-secondary)' }}>{meta?.verb ?? entry.action}</span>
            </>
        );
    }

    return (
        <div className="sa-feed-item">
            <div className="sa-feed-icon" aria-hidden="true">{glyph}</div>
            <div className="sa-feed-body">
                <div className="sa-feed-title">{body}</div>
                <div className="sa-feed-meta" title={absoluteTime(entry.occurred_at)}>
                    {relativeTime(entry.occurred_at)}
                    {entry.metadata?.reason && (
                        <> · <span style={{ color: 'var(--sa-text-secondary)' }}>“{String(entry.metadata.reason)}”</span></>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Page ───────────────────────────────────────────────────────────────────
const DashboardPage: React.FC = () => {
    const history = useHistory();
    const [me, setMe] = useState<Me | null>(null);
    const [data, setData] = useState<DashboardResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const [meRes, statsRes] = await Promise.all([
                    saRequest<{ super_admin: Me }>('GET', 'auth/me'),
                    saRequest<DashboardResponse>('GET', 'dashboard'),
                ]);
                if (cancelled) return;
                setMe(meRes.data.super_admin);
                setData(statsRes.data);
            } catch (err: any) {
                if (cancelled) return;
                if (err?.response?.status === 401) {
                    clearSuperAdminToken();
                    history.replace('/mika/ops/login');
                    return;
                }
                setError(parseSuperAdminError(err).banner ?? 'Failed to load dashboard.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [history]);

    return (
        <SuperAdminLayout currentEmail={me?.email}>
            <div className="sa-page-header">
                <div>
                    <h1>Good {timeOfDay()}{me ? `, ${firstNameOf(me.name)}` : ''}.</h1>
                    <div style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-sm)', marginTop: 4 }}>
                        {me?.last_login_at ? (
                            <>Last signed in <span className="sa-mono">{absoluteTime(me.last_login_at)}</span>
                                {me.last_login_ip && <> from <span className="sa-mono">{me.last_login_ip}</span></>}.</>
                        ) : 'Welcome back.'}
                    </div>
                </div>
                <Link to="/mika/ops/companies/new" className="sa-btn sa-btn-primary" style={{ textDecoration: 'none' }}>
                    + New company
                </Link>
            </div>

            {error && <div className="sa-error-banner">{error}</div>}

            {/* Stat tiles */}
            {loading && !data ? (
                <div className="sa-stat-grid">
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} className="sa-stat">
                            <div className="sa-stat-label" style={{ opacity: 0.4 }}>—</div>
                            <div className="sa-stat-value" style={{ opacity: 0.3 }}>—</div>
                        </div>
                    ))}
                </div>
            ) : data ? (
                <div className="sa-stat-grid">
                    <StatTile primary label="Total tenants" value={data.companies.total} hint="All statuses combined" />
                    <StatTile label="Active" value={data.companies.active} />
                    <StatTile label="Suspended" value={data.companies.suspended} />
                    <StatTile label="Inactive" value={data.companies.inactive} />
                </div>
            ) : null}

            {/* Activity feed */}
            <div className="sa-card">
                <div className="sa-card-header">
                    <h2>Recent activity</h2>
                    <Link
                        to="/mika/ops/companies"
                        className="sa-btn sa-btn-ghost sa-btn-sm"
                        style={{ textDecoration: 'none' }}
                    >
                        View all tenants →
                    </Link>
                </div>

                {loading && !data ? (
                    <div className="sa-feed">
                        {[0, 1, 2, 3, 4].map(i => (
                            <div key={i} className="sa-feed-item">
                                <div className="sa-feed-icon" style={{ opacity: 0.4 }}>·</div>
                                <div className="sa-feed-body">
                                    <div className="sa-skeleton-row" style={{ height: 14, width: '60%', borderRadius: 3 }} />
                                    <div className="sa-skeleton-row" style={{ height: 11, width: '30%', borderRadius: 3, marginTop: 6 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : data && data.recent_audit.length > 0 ? (
                    <div className="sa-feed">
                        {data.recent_audit.map(entry => <FeedItem key={entry.id} entry={entry} />)}
                    </div>
                ) : (
                    <div className="sa-empty">
                        <div className="sa-empty-title">Nothing yet</div>
                        <div className="sa-empty-hint">
                            As you and other Mika Ops users create tenants and perform lifecycle
                            actions, they'll show up here.
                        </div>
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
};

function timeOfDay(): string {
    const h = new Date().getHours();
    if (h < 5) return 'evening';
    if (h < 12) return 'morning';
    if (h < 18) return 'afternoon';
    return 'evening';
}

function firstNameOf(full: string): string {
    return (full || '').trim().split(/\s+/)[0] || '';
}

export default DashboardPage;
